import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import { captureEvent } from '../analytics';

/**
 * A matter returned by the backend `/api/Matter` endpoint.
 *
 * JSON field names (RecordId, Name, ...) are PascalCase to match what the
 * backend serializes; the shape is preserved.
 */
export interface Matter {
    RecordId: string;
    Name: string;
    // Matter landing page fields. Currently synthesized in main.get_matters;
    // optional so the type still describes the backend faithfully.
    ClientName?: string | null;
    ClientNumber?: string | null;
    MatterKey?: string | null;
    Description?: string | null;
    ColorHint?: string | null;
    RecentUpdates?: { documents: number; comments: number } | null;
}

/**
 * The matter returned by `POST /api/matter` (the create endpoint). Unlike the
 * list shape above, this uses the camelCase contract the create endpoint
 * serializes (`MatterResponse`); `id` is the matter's UUID `record_id`.
 */
export interface CreatedMatter {
    id: string;
    name: string;
    description: string | null;
    clientId: string;
    databaseName: string;
}

export interface CreateMatterPayload {
    name: string;
    description?: string | null;
    clientId: string;
    // When true, the backend seeds the new matter database from a backup
    // (pg_restore) before running migrations. Intended for dev/test.
    prepopulate?: boolean;
    // Optional backup filename; backend falls back to a configured default.
    backupName?: string;
}

export const useMatterStore = defineStore('matter', () => {
    const matters = ref<Matter[]>([]);
    const currentMatter = ref<Matter | null>(null);
    const loading = ref(false);
    const creating = ref(false);
    const deleting = ref(false);
    const error = ref<string | null>(null);
    // Server-side backup files available to prepopulate a new matter from.
    // Matter-independent (sourced from PEPPER_BACKUP_DIR); fetched lazily by
    // the create-matter modal so the user can pick which backup to load.
    const availableBackups = ref<string[]>([]);

    const hasMatters = computed(() => matters.value.length > 0);
    const isMatterSelected = computed(() => currentMatter.value !== null);

    async function loadMatters() {
        try {
            loading.value = true;
            error.value = null;
            const response = await fetch('/api/Matter');
            if (!response.ok) {
                throw new Error('Failed to load matters');
            }
            matters.value = await response.json();
        } catch (err) {
            error.value = err instanceof Error ? err.message : String(err);
            throw err;
        } finally {
            loading.value = false;
        }
    }

    /**
     * Create a matter via `POST /api/matter` and return the persisted record.
     * Maps the known error responses to friendly messages: 409 (duplicate
     * matter name) and 404 (unknown client). Tracks its own `creating` flag
     * so it doesn't disturb the list's `loading`/`error` state.
     */
    async function createMatter(payload: CreateMatterPayload): Promise<CreatedMatter> {
        try {
            creating.value = true;
            const response = await fetch('/api/matter', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload),
            });
            if (response.status === 409) {
                throw new Error(`A matter named "${payload.name}" already exists.`);
            }
            if (response.status === 404) {
                throw new Error('The selected client no longer exists.');
            }
            if (!response.ok) {
                throw new Error('Failed to create matter');
            }
            const created = (await response.json()) as CreatedMatter;
            // Onboarding-funnel entry point. ids/booleans only — no name or
            // description (see analytics PII policy).
            captureEvent('matter_created', {
                matter_id: created.id,
                prepopulated: payload.prepopulate === true,
            });
            return created;
        } finally {
            creating.value = false;
        }
    }

    /**
     * Populate `availableBackups` from `GET /api/Backups`. Used by the
     * create-matter modal to populate its (required) backup picker. Failures
     * leave the list empty rather than throwing — the modal surfaces "no
     * backups available" rather than blocking the whole form.
     */
    async function fetchBackups(): Promise<void> {
        try {
            const response = await fetch('/api/Backups');
            if (response.ok) {
                const data = await response.json();
                availableBackups.value = data.backups ?? [];
            }
        } catch (err) {
            console.warn('Failed to fetch available backups:', err);
        }
    }

    /**
     * Permanently delete a matter via `DELETE /api/Matter/{id}`. The backend
     * drops the entire per-matter database (sessions, chats, findings, case
     * data) plus the central matters row — irreversible. On success the matter
     * is removed from the loaded list and deselected if it was the current one.
     * Maps 403 (not a member) / 404 (already gone) to friendly messages.
     */
    async function deleteMatter(matterId: string): Promise<void> {
        try {
            deleting.value = true;
            const response = await fetch(`/api/Matter/${matterId}`, {
                method: 'DELETE',
            });
            if (response.status === 403) {
                throw new Error('You do not have permission to delete this matter.');
            }
            if (response.status === 404) {
                throw new Error('This matter no longer exists.');
            }
            if (!response.ok) {
                throw new Error('Failed to delete matter');
            }
            matters.value = matters.value.filter((m) => m.RecordId !== matterId);
            if (currentMatter.value?.RecordId === matterId) {
                currentMatter.value = null;
            }
            // Destructive, matter-scoped action. ids only — no name (analytics
            // PII policy).
            captureEvent('matter_deleted', { matter_id: matterId });
        } finally {
            deleting.value = false;
        }
    }

    function selectMatter(matter: Matter) {
        currentMatter.value = matter;
    }

    function clearCurrentMatter() {
        currentMatter.value = null;
    }

    /**
     * Hydrate `currentMatter` from a matter id (used by the router when
     * a deep link arrives cold). Loads the matter list if empty, then
     * locates the requested id. Returns false if the id isn't known so
     * the caller can redirect; throws if the list fetch itself fails.
     * No-ops when the requested id is already selected.
     */
    async function ensureMatterLoaded(matterId: string): Promise<boolean> {
        if (currentMatter.value?.RecordId === matterId) return true;
        if (matters.value.length === 0) {
            await loadMatters();
        }
        const found = matters.value.find((m) => m.RecordId === matterId);
        if (!found) return false;
        currentMatter.value = found;
        return true;
    }

    return {
        matters,
        currentMatter,
        loading,
        creating,
        deleting,
        error,
        availableBackups,
        hasMatters,
        isMatterSelected,
        loadMatters,
        createMatter,
        deleteMatter,
        fetchBackups,
        selectMatter,
        clearCurrentMatter,
        ensureMatterLoaded,
    };
});
